import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Database, RefreshCw, Download, ShieldCheck, ShieldAlert, Loader2, X, Check } from "lucide-react";

const STATUS_STYLE = {
  success: { bg: "#DCFCE7", color: "#059669", label: "Success" },
  failed: { bg: "#FEE2E2", color: "#DC2626", label: "Failed" },
  running: { bg: "#FEF0E6", color: "#F97316", label: "Running" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || { bg: "#F3F4F6", color: "#6B7280", label: status || "—" };
  return <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: s.bg, color: s.color }}>{s.label}</span>;
}

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}
function fmtDate(iso) { return iso ? new Date(iso).toLocaleString() : "—"; }

const STALE_MS = 60 * 60 * 1000; // 60 min — matches backend STALE_RUN_MINUTES
function fmtElapsed(fromIso, now) {
  const start = new Date(fromIso).getTime();
  if (!start) return "";
  let s = Math.max(0, Math.floor((now - start) / 1000));
  const h = Math.floor(s / 3600); s -= h * 3600;
  const m = Math.floor(s / 60); s -= m * 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
}

function MfaSetupModal({ onClose, onDone }) {
  const [step, setStep] = useState("qr");
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.post("/admin/mfa/setup").then(({ data }) => setData(data)).catch(() => toast.error("Could not start setup"));
  }, []);
  const activate = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/mfa/activate", { password: "", code: code.trim() });
      toast.success("Two-factor authentication enabled");
      onDone();
    } catch {
      toast.error("Invalid code — check your authenticator app");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(10,31,68,0.6)" }}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" data-testid="mfa-setup-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold">Set up two-factor authentication</h3>
          <button onClick={onClose} data-testid="mfa-modal-close"><X size={18} className="text-[#6B7280]" /></button>
        </div>
        {!data ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin" /></div> : (
          <>
            {step === "qr" && (
              <div className="text-sm text-[#374151] space-y-3">
                <p>1. Scan this QR code with Google Authenticator, Authy, or 1Password:</p>
                <img src={data.qr_data_url} alt="2FA QR" className="mx-auto w-44 h-44 border rounded" data-testid="mfa-qr" />
                <p className="text-xs text-[#6B7280]">Or enter this key manually: <code className="bg-gray-100 px-1 rounded">{data.secret}</code></p>
                <div className="rounded-lg p-3" style={{ backgroundColor: "#FEF0E6" }}>
                  <p className="font-semibold text-[#DC2626] mb-1">Save your recovery codes now — shown only once:</p>
                  <div className="grid grid-cols-2 gap-1 font-mono text-xs" data-testid="mfa-recovery-codes">
                    {data.recovery_codes.map((c) => <span key={c}>{c}</span>)}
                  </div>
                </div>
                <button onClick={() => setStep("verify")} className="w-full rounded-lg px-4 py-2.5 font-semibold text-white" style={{ backgroundColor: "#F97316" }} data-testid="mfa-continue">
                  I've saved them — continue
                </button>
              </div>
            )}
            {step === "verify" && (
              <div className="space-y-3">
                <p className="text-sm text-[#374151]">2. Enter the 6-digit code from your app to confirm:</p>
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="mfa-verify-input"
                  className="w-full rounded-lg px-3 py-2.5 border tracking-widest text-center" style={{ borderColor: "#E5E7EB" }} />
                <button onClick={activate} disabled={busy} data-testid="mfa-activate"
                  className="w-full rounded-lg px-4 py-2.5 font-semibold text-white disabled:opacity-60 flex items-center justify-center gap-2" style={{ backgroundColor: "#F97316" }}>
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Enable 2FA
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SecurityCard() {
  const [status, setStatus] = useState(null);
  const [modal, setModal] = useState(false);
  const load = useCallback(() => api.get("/admin/mfa/status").then(({ data }) => setStatus(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const enabled = status?.enabled;
  return (
    <div className="rounded-xl border p-5 mb-6" style={{ borderColor: "#E5E7EB" }} data-testid="admin-security-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {enabled ? <ShieldCheck size={22} style={{ color: "#059669" }} /> : <ShieldAlert size={22} style={{ color: "#F97316" }} />}
          <div>
            <h3 className="font-semibold">Admin two-factor authentication</h3>
            <p className="text-sm text-[#6B7280]">
              {enabled ? `Enabled · ${status.recovery_remaining} recovery codes remaining` : "Not enabled — your admin login is password-only."}
            </p>
          </div>
        </div>
        <button onClick={() => setModal(true)} data-testid="mfa-setup-button"
          className="rounded-lg px-4 py-2 text-sm font-semibold text-white" style={{ backgroundColor: enabled ? "#0A1F44" : "#F97316" }}>
          {enabled ? "Re-enroll" : "Set up 2FA"}
        </button>
      </div>
      {modal && <MfaSetupModal onClose={() => setModal(false)} onDone={() => { setModal(false); load(); }} />}
    </div>
  );
}

function exportCsv(runs) {
  const cols = ["started_at", "finished_at", "status", "run_type", "archive_bytes", "sha256", "object_key", "locked_until", "error"];
  const lines = [cols.join(",")];
  runs.forEach((r) => lines.push(cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `backups_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
}

export default function AdminBackups() {
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [now, setNow] = useState(Date.now());
  const load = useCallback(() => api.get("/admin/backups").then(({ data }) => setRuns(data.runs)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    const clk = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(t); clearInterval(clk); };
  }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/admin/backup/run");
      toast.success("Backup started — refresh in a moment");
      setTimeout(load, 3000);
    } catch {
      toast.error("Could not start backup");
    } finally { setRunning(false); }
  };

  return (
    <div data-testid="admin-backups">
      <SecurityCard />
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><Database size={22} /> Backups</h2>
        <div className="flex gap-2">
          <button onClick={() => exportCsv(runs)} data-testid="backups-export-csv"
            className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold border" style={{ borderColor: "#E5E7EB" }}>
            <Download size={15} /> Export CSV
          </button>
          <button onClick={runNow} disabled={running} data-testid="backups-run-now"
            className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: "#F97316" }}>
            {running ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Run backup now
          </button>
        </div>
      </div>
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: "#E5E7EB" }}>
        <div className="grid grid-cols-12 px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ backgroundColor: "#0A1F44", color: "#fff" }}>
          <div className="col-span-2">Status</div>
          <div className="col-span-3">Started</div>
          <div className="col-span-1">Type</div>
          <div className="col-span-1">Size</div>
          <div className="col-span-3">Checksum (SHA-256)</div>
          <div className="col-span-2">Locked until</div>
        </div>
        {runs.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-[#9CA3AF]">No backup runs yet.</div>
        ) : runs.map((r) => (
          <div key={r.id} className="grid grid-cols-12 px-4 py-2.5 text-sm border-b last:border-0 items-center" style={{ borderColor: "#F3F4F6" }} data-testid={`backup-row-${r.id}`}>
            <div className="col-span-2">
              <StatusPill status={r.status} />
              {r.status === "running" && (() => {
                const stale = now - new Date(r.started_at).getTime() > STALE_MS;
                return (
                  <span data-testid={`backup-elapsed-${r.id}`} className="block text-[10px] font-semibold mt-0.5"
                    style={{ color: stale ? "#DC2626" : "#F97316" }}>
                    running {fmtElapsed(r.started_at, now)}{stale ? " · likely stuck" : ""}
                  </span>
                );
              })()}
              {r.error && <span title={r.error} className="block text-[10px] text-[#DC2626] truncate mt-0.5">{r.error}</span>}
            </div>
            <div className="col-span-3 text-[#374151]">{fmtDate(r.started_at)}</div>
            <div className="col-span-1 text-[#6B7280] capitalize">{r.run_type}</div>
            <div className="col-span-1 text-[#6B7280]">{fmtBytes(r.archive_bytes)}</div>
            <div className="col-span-3 font-mono text-[11px] text-[#6B7280] truncate" title={r.sha256 || ""}>{r.sha256 || "—"}</div>
            <div className="col-span-2 text-[#6B7280]">{fmtDate(r.locked_until)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
